/**
 * BannersScreen — manage the home-page banner images (add / edit / delete /
 * reorder / show-hide).
 *
 * - Upload posts multipart to `/api/banners/upload/` (REST, not GraphQL —
 *   same Strawberry multipart limitation as the gallery). The backend
 *   transcodes every upload to WebP and downscales it — see
 *   backend/banners/models.py.
 * - Multiple banners are supported; the public home page rotates through the
 *   active ones in this order. Drag rows to reorder.
 */
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  GripVertical,
  Image as ImageIcon,
  Loader2,
  Pencil,
  Trash2,
  Upload as UploadIcon,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "urql";

import { getToken } from "../auth/token";
import { cn } from "../lib/cn";
import { confirmDestructive, showError, showSuccess } from "../lib/sweet";

// ---------------------------------------------------------------------------
// GraphQL
// ---------------------------------------------------------------------------

const ADMIN_BANNERS_QUERY = /* GraphQL */ `
  query AdminBanners {
    adminBanners {
      id
      altText
      altTextKu
      altTextAr
      linkUrl
      isActive
      sortOrder
      imageUrl
      width
      height
    }
  }
`;

const UPDATE_BANNER = /* GraphQL */ `
  mutation UpdateBanner($id: ID!, $input: UpdateBannerInput!) {
    updateBanner(bannerId: $id, input: $input) {
      id
      altText
      altTextKu
      altTextAr
      linkUrl
      isActive
      sortOrder
    }
  }
`;

const DELETE_BANNER = /* GraphQL */ `
  mutation DeleteBanner($id: ID!) {
    deleteBanner(bannerId: $id)
  }
`;

const REORDER_BANNERS = /* GraphQL */ `
  mutation ReorderBanners($ids: [ID!]!) {
    reorderBanners(bannerIds: $ids) {
      id
      sortOrder
    }
  }
`;

// Banner upload uses a dedicated REST endpoint (same reason as the gallery).
const API_ORIGIN = (() => {
  const env = import.meta.env.VITE_GRAPHQL_URL;
  if (env) return new URL(env).origin;
  return import.meta.env.PROD ? "https://api.kurdishrojava.org.uk" : "";
})();

const UPLOAD_URL = `${API_ORIGIN}/api/banners/upload/`;

function absMediaUrl(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${API_ORIGIN}${url}`;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BannerRow {
  id: string;
  altText: string;
  altTextKu: string | null;
  altTextAr: string | null;
  linkUrl: string | null;
  isActive: boolean;
  sortOrder: number;
  imageUrl: string | null;
  width: number | null;
  height: number | null;
}

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

export function BannersScreen() {
  const [{ data, fetching, error }, refetch] = useQuery<{ adminBanners: BannerRow[] }>({
    query: ADMIN_BANNERS_QUERY,
    requestPolicy: "cache-and-network",
  });

  const [, reorderBanners] = useMutation(REORDER_BANNERS);

  // Local copy so drag updates feel instant; synced to the server on drop.
  const [ordered, setOrdered] = useState<BannerRow[]>([]);
  const serverList = data?.adminBanners;

  // Adopt the server list whenever the *set* of banner IDs changes (an add or
  // delete) — but not on every refetch, so an in-progress reorder isn't
  // clobbered before the server confirms it.
  const serverIdKey = serverList
    ? [...serverList.map((b) => b.id)].sort().join(",")
    : "";
  useEffect(() => {
    if (!serverList) return;
    setOrdered((prev) => {
      const prevKey = [...prev.map((b) => b.id)].sort().join(",");
      return prevKey === serverIdKey ? prev : serverList;
    });
  }, [serverIdKey, serverList]);

  const list = ordered.length ? ordered : (serverList ?? []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  async function handleDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = list.findIndex((b) => b.id === active.id);
    const newIndex = list.findIndex((b) => b.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(list, oldIndex, newIndex);
    setOrdered(next);
    const result = await reorderBanners({ ids: next.map((b) => b.id) });
    if (result.error) {
      setOrdered(list);
      await showError({ title: "Couldn't save order", text: result.error.message });
    }
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <header>
        <h1 className="text-xl font-semibold text-stone-900">Home page banners</h1>
        <p className="mt-1 text-sm text-stone-500">
          Images shown at the top of the home page. Add as many as you like — the
          active ones rotate in the order below. Drag the handle to reorder.
        </p>
      </header>

      <UploadForm onUploaded={() => refetch({ requestPolicy: "network-only" })} />

      {fetching && !data ? (
        <div className="rounded-lg border border-stone-200 bg-white p-12 text-center text-stone-500">
          <Loader2 className="mx-auto size-5 animate-spin" />
        </div>
      ) : error ? (
        <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
          {error.message}
        </div>
      ) : list.length === 0 ? (
        <div className="rounded-lg border border-dashed border-stone-300 bg-stone-50 p-12 text-center text-sm text-stone-500">
          No banners yet. Upload one above.
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={list.map((b) => b.id)} strategy={verticalListSortingStrategy}>
            <ul className="space-y-3">
              {list.map((banner) => (
                <BannerCard
                  key={banner.id}
                  banner={banner}
                  onChanged={() => refetch({ requestPolicy: "network-only" })}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Banner row
// ---------------------------------------------------------------------------

function BannerCard({ banner, onChanged }: { banner: BannerRow; onChanged: () => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: banner.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    opacity: isDragging ? 0.9 : 1,
  };

  const [editing, setEditing] = useState(false);
  const [, updateBanner] = useMutation(UPDATE_BANNER);
  const [, deleteBanner] = useMutation(DELETE_BANNER);

  async function toggleActive() {
    const result = await updateBanner({ id: banner.id, input: { isActive: !banner.isActive } });
    if (result.error) {
      await showError({ title: "Couldn't update", text: result.error.message });
      return;
    }
    onChanged();
  }

  async function handleDelete() {
    const ok = await confirmDestructive({
      title: "Delete this banner?",
      text: `"${banner.altText}"\n\nThis removes the image from the server. It cannot be undone.`,
      confirmText: "Yes, delete",
    });
    if (!ok) return;
    const result = await deleteBanner({ id: banner.id });
    if (result.error) {
      await showError({ title: "Couldn't delete", text: result.error.message });
      return;
    }
    onChanged();
    showSuccess({ title: "Deleted" });
  }

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        "rounded-lg border bg-white shadow-sm",
        isDragging ? "border-flag-red ring-2 ring-flag-red/30" : "border-stone-200",
      )}
    >
      <div className="flex items-stretch gap-3 p-3">
        {/* Drag handle */}
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="flex shrink-0 cursor-grab touch-none items-center px-1 text-stone-400 hover:text-stone-600 active:cursor-grabbing"
          aria-label={`Reorder ${banner.altText}`}
        >
          <GripVertical className="size-5" />
        </button>

        {/* Preview */}
        <div className="h-20 w-32 shrink-0 overflow-hidden rounded-md bg-stone-100">
          {banner.imageUrl ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={absMediaUrl(banner.imageUrl)}
              alt={banner.altText}
              className="size-full object-cover"
              draggable={false}
            />
          ) : (
            <div className="flex size-full items-center justify-center text-stone-300">
              <ImageIcon className="size-6" />
            </div>
          )}
        </div>

        {/* Details */}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-stone-900" title={banner.altText}>
            {banner.altText}
          </p>
          {banner.linkUrl ? (
            <p className="truncate text-xs text-stone-500" title={banner.linkUrl}>
              Links to: {banner.linkUrl}
            </p>
          ) : (
            <p className="text-xs text-stone-400">No link</p>
          )}
          {banner.width && banner.height ? (
            <p className="mt-0.5 text-xs text-stone-400">
              {banner.width}×{banner.height} · WebP
            </p>
          ) : null}
        </div>

        {/* Actions */}
        <div className="flex shrink-0 flex-col items-end justify-between gap-2">
          <label className="flex cursor-pointer items-center gap-2 text-xs text-stone-600">
            <input
              type="checkbox"
              checked={banner.isActive}
              onChange={toggleActive}
              className="size-4 accent-[var(--color-flag-green)]"
            />
            {banner.isActive ? "Visible" : "Hidden"}
          </label>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setEditing((v) => !v)}
              className="inline-flex size-8 items-center justify-center rounded-md border border-stone-200 text-stone-500 hover:bg-stone-50 hover:text-stone-700"
              aria-label="Edit banner"
            >
              {editing ? <X className="size-4" /> : <Pencil className="size-4" />}
            </button>
            <button
              type="button"
              onClick={handleDelete}
              className="inline-flex size-8 items-center justify-center rounded-md border border-stone-200 text-stone-500 hover:bg-red-50 hover:text-red-700"
              aria-label="Delete banner"
            >
              <Trash2 className="size-4" />
            </button>
          </div>
        </div>
      </div>

      {editing && (
        <EditBannerForm
          banner={banner}
          onSaved={() => {
            setEditing(false);
            onChanged();
          }}
          onCancel={() => setEditing(false)}
        />
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Inline edit form (alt text + translations + link)
// ---------------------------------------------------------------------------

function EditBannerForm({
  banner,
  onSaved,
  onCancel,
}: {
  banner: BannerRow;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [altText, setAltText] = useState(banner.altText);
  const [altTextKu, setAltTextKu] = useState(banner.altTextKu ?? "");
  const [altTextAr, setAltTextAr] = useState(banner.altTextAr ?? "");
  const [linkUrl, setLinkUrl] = useState(banner.linkUrl ?? "");
  const [saving, setSaving] = useState(false);
  const [, updateBanner] = useMutation(UPDATE_BANNER);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!altText.trim()) {
      await showError({ title: "Alt text is required", text: "Screen readers rely on it." });
      return;
    }
    setSaving(true);
    try {
      const result = await updateBanner({
        id: banner.id,
        input: {
          altText: altText.trim(),
          altTextKu: altTextKu.trim(),
          altTextAr: altTextAr.trim(),
          linkUrl: linkUrl.trim(),
        },
      });
      if (result.error) {
        await showError({ title: "Couldn't save", text: result.error.message });
        return;
      }
      onSaved();
    } finally {
      setSaving(false);
    }
  }

  const inputClass =
    "w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500";

  return (
    <form onSubmit={handleSave} className="space-y-3 border-t border-stone-200 bg-stone-50 p-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <label className="block text-xs font-medium text-stone-600">
          Alt text (English) *
          <input className={cn(inputClass, "mt-1")} value={altText} onChange={(e) => setAltText(e.target.value)} />
        </label>
        <label className="block text-xs font-medium text-stone-600">
          Alt text (Kurmancî)
          <input className={cn(inputClass, "mt-1")} value={altTextKu} onChange={(e) => setAltTextKu(e.target.value)} />
        </label>
        <label className="block text-xs font-medium text-stone-600">
          Alt text (العربية)
          <input dir="rtl" className={cn(inputClass, "mt-1")} value={altTextAr} onChange={(e) => setAltTextAr(e.target.value)} />
        </label>
      </div>
      <label className="block text-xs font-medium text-stone-600">
        Link URL (optional) — banner becomes clickable
        <input
          type="url"
          placeholder="https://…"
          className={cn(inputClass, "mt-1")}
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
        />
      </label>
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-md bg-flag-red px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-60"
        >
          {saving && <Loader2 className="size-4 animate-spin" />}
          {saving ? "Saving…" : "Save changes"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-stone-300 bg-white px-4 py-2 text-sm text-stone-700 hover:bg-stone-50"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Upload form
// ---------------------------------------------------------------------------

function UploadForm({ onUploaded }: { onUploaded: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [altText, setAltText] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!file) {
      await showError({ title: "Pick a file first" });
      return;
    }
    if (!altText.trim()) {
      await showError({ title: "Alt text is required", text: "Screen readers rely on it." });
      return;
    }
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append("alt_text", altText.trim());
      if (linkUrl.trim()) form.append("link_url", linkUrl.trim());
      form.append("file", file, file.name);

      const token = getToken();
      const headers: Record<string, string> = {};
      if (token) headers.Authorization = `JWT ${token}`;
      // Don't set Content-Type — the browser sets the multipart boundary.

      const res = await fetch(UPLOAD_URL, { method: "POST", headers, body: form });
      if (!res.ok) {
        let message = `${res.status} ${res.statusText}`;
        try {
          const body = await res.json();
          if (body?.errors?.[0]?.message) message = body.errors[0].message;
        } catch {
          /* not JSON */
        }
        await showError({ title: "Upload failed", text: message });
        return;
      }

      setFile(null);
      setAltText("");
      setLinkUrl("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      onUploaded();
      showSuccess({ title: "Banner uploaded" });
    } catch (err) {
      await showError({
        title: "Upload failed",
        text: err instanceof Error ? err.message : "Network error",
      });
    } finally {
      setSubmitting(false);
    }
  }

  const inputClass =
    "w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500";

  return (
    <form onSubmit={handleSubmit} className="rounded-lg border border-stone-200 bg-white p-4">
      <h2 className="mb-3 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-stone-500">
        <UploadIcon className="size-3.5" />
        Add a banner
      </h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-stone-300 bg-white px-3 py-2 text-sm hover:bg-stone-50">
          <ImageIcon className="size-4 text-stone-500" />
          <span className="truncate">{file ? file.name : "Choose image"}</span>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </label>
        <input
          type="text"
          value={altText}
          onChange={(e) => setAltText(e.target.value)}
          placeholder="Alt text (required) — describe the image"
          className={inputClass}
        />
        <input
          type="url"
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
          placeholder="Link URL (optional)"
          className={inputClass}
        />
        <button
          type="submit"
          disabled={submitting || !file || !altText.trim()}
          className={cn(
            "inline-flex items-center justify-center gap-2 rounded-md bg-flag-red px-4 py-2 text-sm font-medium text-white",
            "hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60",
          )}
        >
          {submitting && <Loader2 className="size-4 animate-spin" />}
          {submitting ? "Uploading…" : "Upload banner"}
        </button>
      </div>
      <p className="mt-2 text-xs text-stone-500">
        JPEG / PNG / WebP / GIF, up to 20 MB. Every upload is converted to WebP and
        resized for fast loading. Wide landscape images work best.
      </p>
    </form>
  );
}
